import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Site-wide SEO constants. Any change to the canonical origin, brand
 * name, or default share image should be made here — never inline.
 */
export const SITE = {
  origin: 'https://adauraawards.com',
  name: 'AdAura Awards',
  legalName: 'AdAura Awards 2026',
  publisher: 'Marketing Mind',
  defaultImage: 'https://adauraawards.com/og-image.png',
  twitter: '@MarketingMindIn',
  locale: 'en-IN',
};

/**
 * <Seo> — one component, every page.
 *
 * Wires up per-page <title>, meta description, canonical URL, Open Graph,
 * Twitter Cards, and (optionally) one or more JSON-LD structured-data
 * blocks. Kept intentionally small: pages pass in only what's unique;
 * everything else falls back to sensible defaults defined above.
 *
 * @param title        — full <title>; if omitted, index.html default kicks in.
 * @param description  — meta description (150-160 chars ideal).
 * @param path         — page path (e.g. "/about"). Used to build canonical + og:url.
 * @param image        — absolute URL for og:image / twitter:image. Defaults to site OG.
 * @param imageAlt     — alt text for og:image (accessibility + SEO).
 * @param noindex      — set true for legal/admin/portal pages we don't want in the index.
 * @param jsonLd       — one object or array of JSON-LD schema objects.
 * @param keywords     — optional page-scoped keywords string (kept for tooling, low SEO weight).
 */
export const Seo = ({
  title,
  description,
  path = '/',
  image = SITE.defaultImage,
  imageAlt,
  noindex = false,
  jsonLd = null,
  keywords,
}) => {
  const canonical = `${SITE.origin}${path.startsWith('/') ? path : `/${path}`}`;
  const schemas = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large, max-snippet:-1'} />
      {/* Open Graph */}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      {/* Twitter */}
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}
      {/* Structured data — one <script> per JSON-LD block for maximum
          crawler compatibility (Googlebot merges them). */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
};

/* -------------- schema factory helpers -------------- */

/** BreadcrumbList — pass an array of {name, path} in order. */
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE.origin}${it.path.startsWith('/') ? it.path : `/${it.path}`}`,
  })),
});

/** WebPage schema — plug into any interior page. */
export const webPageSchema = ({ path, name, description }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE.origin}${path}#webpage`,
  url: `${SITE.origin}${path}`,
  name,
  description,
  isPartOf: { '@id': `${SITE.origin}/#website` },
  publisher: { '@id': `${SITE.origin}/#organization` },
  inLanguage: SITE.locale,
});

/** FAQPage — pass an array of {q, a}. Google surfaces these as rich results. */
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});

/** Event schema — for the AdAura Awards 2026 as a business event. */
export const eventSchema = ({ startDate, endDate, url, description, venueName, venueLocality }) => ({
  '@context': 'https://schema.org',
  '@type': 'BusinessEvent',
  name: 'AdAura Awards 2026',
  description,
  startDate,
  endDate,
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  eventStatus: 'https://schema.org/EventScheduled',
  location: {
    '@type': 'Place',
    name: venueName || 'India',
    address: {
      '@type': 'PostalAddress',
      addressLocality: venueLocality || 'New Delhi',
      addressCountry: 'IN',
    },
  },
  organizer: { '@id': `${SITE.origin}/#organization` },
  url: url || SITE.origin,
  image: [SITE.defaultImage],
  offers: {
    '@type': 'Offer',
    url: `${SITE.origin}/enter`,
    price: '7500',
    priceCurrency: 'INR',
    availability: 'https://schema.org/InStock',
    validFrom: startDate,
  },
});

/** ItemList — used on the Categories page to enumerate award categories. */
export const itemListSchema = (items, listName = 'Award Categories') => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: listName,
  itemListOrder: 'https://schema.org/ItemListOrderAscending',
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    url: it.url ? `${SITE.origin}${it.url}` : undefined,
  })),
});

/**
 * Award schema (schema.org/Award) — describes an individual award or
 * category. Not currently emitted anywhere in the app (no winners yet
 * on the public site), but kept ready so we can plug it into the
 * Categories page + winner announcement pages the moment those go live.
 *
 * @param name         — e.g. "Best Brand Film of the Year 2026"
 * @param description  — 1-2 sentence description of what the award recognises
 * @param awardedBy    — presenter (defaults to AdAura Awards Organization)
 * @param dateAwarded  — ISO date the award is/was given, when known
 * @param url          — canonical URL of the category/winner page
 */
export const awardSchema = ({ name, description, awardedBy, dateAwarded, url }) => {
  const doc = {
    '@context': 'https://schema.org',
    '@type': 'Award',
    name,
    description,
    awardedBy: awardedBy || { '@id': `${SITE.origin}/#organization` },
  };
  if (dateAwarded) doc.dateAwarded = dateAwarded;
  if (url) doc.url = url.startsWith('http') ? url : `${SITE.origin}${url}`;
  return doc;
};

export default Seo;
